// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { EducBox, ResultCard, InputRow, fmt, calcIRPEF } from './shared';
import { useDeepLinkParams, hasAllParams, ShareCalcButton, scrollToResult } from './deepLink';

// Coefficienti ATECO 2025 (in vigore dal 2026): Professionisti 78% — Commercio 54%
const COEFFICIENTI = { professionista: 0.78, commercio: 0.54, artigiano: 0.67, servizi: 0.67 };

const calcForfettario = ({ fatturato, categoria, primiAnni }) => {
  const F = parseFloat(fatturato) || 0;
  const coeff = COEFFICIENTI[categoria] || 0.78;
  const imponibile = F * coeff;
  // Gestione Separata INPS 2026: 26,07% (aggiornato da 26,23%)
  const inpsBase = imponibile * 0.2607;
  const inps = inpsBase * 0.65;
  const aliquotaImposta = primiAnni ? 0.05 : 0.15;
  const imposta = imponibile * aliquotaImposta;
  const netto = F - inps - imposta;
  const aliquotaEff = F > 0 ? ((inps + imposta) / F) * 100 : 0;
  return { netto, nettoMensile: netto / 12, inps, imposta, imponibile, aliquotaEff };
};

const calcOrdinario = ({ fatturato, spese, contributiManuale }) => {
  const F = parseFloat(fatturato) || 0;
  const S = parseFloat(spese) || 0;
  const inpsBase = (F - S) * 0.2623;
  const inps = contributiManuale !== null ? parseFloat(contributiManuale) || inpsBase : inpsBase;
  const imponibile = Math.max(0, F - S - inps);
  const irpef = calcIRPEF(imponibile);
  const netto = F - S - inps - irpef;
  const aliquotaEff = F > 0 ? ((inps + irpef) / F) * 100 : 0;
  return { netto, nettoMensile: netto / 12, inps, imposta: irpef, imponibile, aliquotaEff };
};

const PremiumGateInline = ({ onClose }) => {
  const navigate = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)' }}
      onClick={onClose}
    >
      <div className="rounded-2xl p-8 max-w-sm w-full text-center" style={{ background: '#1C1C1A', border: '1px solid rgba(201,168,76,0.4)' }} onClick={e => e.stopPropagation()}>
        <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)' }}>
          <Lock className="w-6 h-6 text-[#C9A84C]" />
        </div>
        <h3 className="text-lg font-bold text-[#E8E8E4] mb-2">Funzionalità Premium</h3>
        <p className="text-sm text-[#888780] mb-1">Il <strong className="text-[#C9A84C]">regime ordinario IRPEF</strong> è disponibile con SAPIENS Premium. (Il forfettario 5% per i primi 5 anni è gratuito.)</p>
        <p className="text-2xl font-bold text-[#C9A84C] mt-4 mb-1">€4,99<span className="text-sm font-normal text-[#888780]">/mese</span></p>
        <p className="text-xs text-[#888780] mb-6">Accesso a tutti i calcolatori avanzati + Report PDF personalizzato</p>
        <button onClick={() => navigate('/premium')} className="w-full py-3 rounded-xl font-semibold text-sm sapiens-btn-primary mb-3">Scopri Premium</button>
        <button onClick={onClose} className="w-full py-2 text-sm text-[#888780] hover:text-[#E8E8E4] transition-colors">Non ora</button>
      </div>
    </motion.div>
  );
};

const PartitaIVA = () => {
  const isPremium = sessionStorage.getItem('admin_preview_premium') === 'true';

  // ── Deep-link wiring (Deployment 13) ────────────────────────────────────
  const url = useDeepLinkParams({
    fatturato: 'num',
    regime: ['enum', 'forfettario', 'ordinario'],
    ateco: ['enum', 'IT', 'commercio', 'artigiano', 'altri'],
  });
  // Map URL ateco → internal categoria (IT = intellettuali professionali = professionista)
  const ATECO_TO_CAT = { IT: 'professionista', commercio: 'commercio', artigiano: 'artigiano', altri: 'servizi' };

  const [fatturato, setFatturato] = useState(url.fatturato ?? '30000');
  const [regime, setRegime] = useState(url.regime ?? 'forfettario');
  const [categoria, setCategoria] = useState(url.ateco ? ATECO_TO_CAT[url.ateco] : 'professionista');
  const [primiAnni, setPrimiAnni] = useState(false);
  const [spese, setSpese] = useState('0');
  const [contributiManuale, setContributiManuale] = useState('');
  const [showGate, setShowGate] = useState(false);

  useEffect(() => {
    if (hasAllParams(url, ['fatturato', 'regime', 'ateco'])) scrollToResult('piva-result');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deployment 14 — P4: forfettario 5% (primi 5 anni) ora GRATUITO; ordinario IRPEF resta Premium.
  const forf = useMemo(() => calcForfettario({ fatturato, categoria, primiAnni }), [fatturato, categoria, primiAnni]);
  const ord = useMemo(() => calcOrdinario({ fatturato, spese, contributiManuale: contributiManuale !== '' ? contributiManuale : null }), [fatturato, spese, contributiManuale]);
  const current = regime === 'forfettario' ? forf : ord;
  const winner = forf.netto >= ord.netto ? 'forfettario' : 'ordinario';

  const handlePrimiAnni = () => {
    setPrimiAnni(!primiAnni);
  };

  const handleOrdinario = () => {
    if (!isPremium) { setShowGate(true); return; }
    setRegime(regime === 'ordinario' ? 'forfettario' : 'ordinario');
  };

  const Row = ({ label, fv, ov }) => (
    <tr className="border-b border-white/[0.04]">
      <td className="py-2.5 text-sm text-[#888780]">{label}</td>
      <td className={`py-2.5 text-sm text-right font-medium ${winner === 'forfettario' ? 'text-[#3D7A52]' : 'text-[#E8E8E4]'}`}>€{fmt(fv)}</td>
      {isPremium && <td className={`py-2.5 text-sm text-right font-medium ${winner === 'ordinario' ? 'text-[#3D7A52]' : 'text-[#E8E8E4]'}`}>€{fmt(ov)}</td>}
    </tr>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="partita-iva-calc">
      <AnimatePresence>{showGate && <PremiumGateInline onClose={() => setShowGate(false)} />}</AnimatePresence>

      <EducBox text="Aprire una Partita IVA cambia completamente il calcolo del reddito netto. Il Forfettario 15% è disponibile gratuitamente. Attiva Premium per simulare il regime al 5% (primi 5 anni) e il confronto con il regime Ordinario IRPEF. Limite forfettario 2026: €85.000/anno. Gestione Separata INPS 2026: 26,07%." />

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <InputRow label="Fatturato annuo (€)" value={fatturato} onChange={setFatturato} prefix="€" />
        <div>
          <label className="text-[#888780] text-sm block mb-1">Categoria attività</label>
          <select value={categoria} onChange={e => setCategoria(e.target.value)} className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm" data-testid="categoria-select">
            <option value="professionista">Professionista (coeff. 78%)</option>
            <option value="artigiano">Artigiano / Servizi (coeff. 67%)</option>
            <option value="commercio">Commercio (coeff. 54%)</option>
            <option value="servizi">Altri servizi (coeff. 67%)</option>
          </select>
        </div>
        {isPremium && <>
          <InputRow label="Spese deducibili annue (€)" value={spese} onChange={setSpese} prefix="€" tooltip="Solo per regime ordinario" />
          <div>
            <label className="text-[#888780] text-sm block mb-1">Contributi INPS annui (€)</label>
            <input type="number" value={contributiManuale} onChange={e => setContributiManuale(e.target.value)} placeholder="Auto-calcolato" className="w-full bg-[#1C1C1A] border border-white/[0.08] text-[#E8E8E4] rounded-md px-3 py-2 text-sm" />
          </div>
        </>}
      </div>

      {/* Regime selector */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setRegime('forfettario')} data-testid="regime-forfettario"
          className="px-4 py-2 rounded-xl text-sm font-medium transition-all"
          style={{ background: regime === 'forfettario' ? '#C9A84C' : '#252523', color: regime === 'forfettario' ? '#3A2800' : '#888780', border: `1px solid ${regime === 'forfettario' ? '#C9A84C' : 'rgba(255,255,255,0.08)'}` }}>
          Forfettario 15% <span className="text-xs opacity-70">FREE</span>
        </button>
        <button onClick={handlePrimiAnni} data-testid="primi-anni-toggle"
          className="px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2"
          style={{ background: primiAnni && isPremium ? 'rgba(201,168,76,0.2)' : '#252523', color: '#888780', border: `1px solid ${primiAnni && isPremium ? 'rgba(201,168,76,0.5)' : 'rgba(255,255,255,0.08)'}` }}>
          {!isPremium && <Lock className="w-3 h-3" />}
          Forfettario 5% (primi 5 anni) <span className="text-xs text-[#C9A84C]">PREMIUM</span>
        </button>
        <button onClick={handleOrdinario} data-testid="regime-ordinario"
          className="px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2"
          style={{ background: regime === 'ordinario' && isPremium ? '#C9A84C' : '#252523', color: regime === 'ordinario' && isPremium ? '#3A2800' : '#888780', border: `1px solid ${regime === 'ordinario' && isPremium ? '#C9A84C' : 'rgba(255,255,255,0.08)'}` }}>
          {!isPremium && <Lock className="w-3 h-3" />}
          Ordinario IRPEF <span className="text-xs text-[#C9A84C]">PREMIUM</span>
        </button>
      </div>

      {/* Current results */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6" data-testid="piva-result">
        <div className="col-span-2 sm:col-span-1">
          <ResultCard label="Reddito netto annuo" value={`€${fmt(current.netto)}`} green />
        </div>
        <ResultCard label="Reddito netto mensile" value={`€${fmt(current.nettoMensile)}`} green />
        <ResultCard label="Aliquota effettiva totale" value={`${current.aliquotaEff.toFixed(1)}%`} sub="Tasse / Fatturato" />
        <ResultCard label="Contributi INPS annui" value={`€${fmt(current.inps)}`} />
        <ResultCard label={regime === 'forfettario' ? `Imposta sostitutiva (${primiAnni && isPremium ? '5' : '15'}%)` : 'IRPEF'} value={`€${fmt(current.imposta)}`} />
        <ResultCard label="Imponibile fiscale" value={`€${fmt(current.imponibile)}`} />
      </div>

      {/* Tabella comparativa — solo per premium */}
      {isPremium ? (
        <div className="sapiens-card p-5 mb-5">
          <p className="text-sm font-semibold text-[#E8E8E4] mb-3">Confronto regimi fiscali</p>
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/[0.08]">
                <th className="text-left py-2 text-xs text-[#888780]"></th>
                <th className={`text-right py-2 text-xs font-semibold ${winner === 'forfettario' ? 'text-[#3D7A52]' : 'text-[#888780]'}`}>Forfettario {winner === 'forfettario' && '✓'}</th>
                <th className={`text-right py-2 text-xs font-semibold ${winner === 'ordinario' ? 'text-[#3D7A52]' : 'text-[#888780]'}`}>Ordinario {winner === 'ordinario' && '✓'}</th>
              </tr>
            </thead>
            <tbody>
              <Row label="Fatturato" fv={parseFloat(fatturato)||0} ov={parseFloat(fatturato)||0} />
              <Row label="Contributi INPS" fv={forf.inps} ov={ord.inps} />
              <Row label="Tasse" fv={forf.imposta} ov={ord.imposta} />
              <tr className="border-t-2 border-white/[0.12]">
                <td className="py-2.5 text-sm font-bold text-[#E8E8E4]">Netto annuo</td>
                <td className={`py-2.5 text-sm text-right font-bold ${winner === 'forfettario' ? 'text-[#3D7A52]' : 'text-[#E8E8E4]'}`}>€{fmt(forf.netto)}</td>
                <td className={`py-2.5 text-sm text-right font-bold ${winner === 'ordinario' ? 'text-[#3D7A52]' : 'text-[#E8E8E4]'}`}>€{fmt(ord.netto)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mb-5 p-5 rounded-xl text-center" style={{ background: 'rgba(201,168,76,0.05)', border: '1px dashed rgba(201,168,76,0.3)' }}>
          <Lock className="w-5 h-5 text-[#C9A84C] mx-auto mb-2" />
          <p className="text-sm text-[#888780]">Il confronto tra regimi (5% / 15% / Ordinario) è disponibile con <strong className="text-[#C9A84C]">SAPIENS Premium</strong> a €4,99/mese</p>
          <button onClick={() => setShowGate(true)} className="mt-3 px-4 py-2 rounded-lg text-xs sapiens-btn-primary">Scopri Premium</button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div className="p-4 rounded-xl text-sm" style={{ background: 'rgba(61,122,82,0.08)', border: '1px solid rgba(61,122,82,0.25)' }}>
          <p className="font-medium text-[#3D7A52] mb-1">✓ Forfettario consigliato se:</p>
          <p className="text-[#888780] leading-relaxed">Poche spese deducibili, fatturato sotto €85.000, inizio attività</p>
        </div>
        <div className="p-4 rounded-xl text-sm" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)' }}>
          <p className="font-medium text-[#C9A84C] mb-1">Ordinario conviene se:</p>
          <p className="text-[#888780] leading-relaxed">Molte spese deducibili (&gt;30% del fatturato) o fatturato oltre €85.000</p>
        </div>
      </div>

      <ShareCalcButton slug="partita-iva" testid="partita-iva-share-btn"
        getValues={() => ({
          fatturato,
          regime,
          ateco: Object.entries({ IT: 'professionista', commercio: 'commercio', artigiano: 'artigiano', altri: 'servizi' }).find(([, v]) => v === categoria)?.[0] || 'IT',
        })} />

      <p className="text-xs text-[#888780] mt-3 leading-relaxed" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 12 }}>
        Calcolo indicativo. Le aliquote INPS variano per categoria e anzianità contributiva. Consulta un commercialista per la scelta ottimale.
      </p>
</motion.div>
  );
};

export default PartitaIVA;
